import React, { useState } from 'react';
import {Button, Group, Radio, ScrollArea, Stack, Text, Title} from '@mantine/core';
import SCModal from '@/PageComponents/Modal/SCModal';
import { LocationDTO } from '@/interfaces/api/models';

// Asks which location becomes the primary of a type when the current primary gives it up: its
// Primary box is unticked, or its type is changed. Cancelling changes nothing; the chosen location
// is made primary when the location being edited is saved.
type Props = {
  location: LocationDTO;
  typeName: string; // the type that needs a new primary, e.g. "delivery"
  newTypeName?: string; // set when the location is being changed to another type
  candidates: LocationDTO[];
  onConfirm: (replacement: LocationDTO) => void;
  onCancel: () => void;
};

const describe = (x: LocationDTO) => [x.Description, x.LocationDisplay].filter(Boolean).join(' - ');

const LocationPrimaryReplacementModal = ({ location, typeName, newTypeName, candidates, onConfirm, onCancel }: Props) => {
  const [selectedId, setSelectedId] = useState<string | null>(candidates.length === 1 ? (candidates[0].ID as string) : null);
  const replacement = candidates.find((x) => x.ID === selectedId) ?? null;

  return (
    <SCModal open={true} decor={'none'} onClose={onCancel} size={'md'}>
      <Stack gap={'md'} data-testid={'location-primary-replacement-modal'}>
        <Title size={'md'} fw={600}>Select new primary {typeName} address</Title>
        <Text size={'sm'}>
          {newTypeName
            ? <>{location.Description} is the primary {typeName} address. Changing it to a {newTypeName} address leaves the
              other {typeName} addresses without a primary.</>
            : <>{location.Description} is the primary {typeName} address.</>}
          {' '}Select which address should become the primary {typeName} address. The change is made when you save.
        </Text>
        <ScrollArea.Autosize mah={'clamp(200px, 50vh, 400px)'}>
          <Radio.Group value={selectedId} onChange={setSelectedId} aria-label={`New primary ${typeName} address`}>
            <Stack gap={'xs'}>
              {candidates.map((x) => (
                <Radio key={x.ID} value={x.ID as string} label={describe(x)} color={'scBlue'} />
              ))}
            </Stack>
          </Radio.Group>
        </ScrollArea.Autosize>
        <Group mt={'lg'} justify={'right'} gap={'xs'}>
          <Button type={'button'} variant={'subtle'} color={'gray.9'} onClick={onCancel}>
            Cancel
          </Button>
          <Button color={'scBlue'} disabled={!replacement} onClick={() => replacement && onConfirm(replacement)}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </SCModal>
  );
};

export default LocationPrimaryReplacementModal;
